import { axiosKK } from '../services/networkRequest';
import SubmitButton from './SubmitButton';
import React, { useState } from 'react';
import { RouteComponentProps } from 'react-router';
import { XMarkIcon, ArrowLeftIcon } from '@heroicons/react/24/solid';
import errorMessage from '../services/errorMessage';
import { User } from '../contexts/UserContext';
import { Link } from 'react-router-dom';
import useGtmEventTracking from './useGtmEventTracking';
import { adminCheck } from '../utilities/Role';
import { BoxData, Dimensions, Product } from '../models/APIModels';
import { Address } from './AddressLookup';
import Button from './Button';

export interface LoginProps extends RouteComponentProps {
  postLogin: (user: User) => void;
  close?: () => void;
  showToggle?: boolean;
}

interface ConfirmationState {
  boxes: BoxData[];
  collectionAddress: Address;
  collectionDate: Date;
  deliveryAddress: Address;
  dontKnowAddress: boolean;
  deliveryDate: Date;
  dontKnowDate: boolean;
  storageRequired: boolean;
  suitcases?: Dimensions[];
  suitcaseData?: Product;
  isFirstTimeTriggered?: boolean;
  fromConfirmation?: boolean;
}

export default function LoginSection({
  history,
  postLogin,
  close,
  showToggle = true,
}: LoginProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [networkError, setNetworkError] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({});

  const confirmationState = history.location.state as ConfirmationState;
  const sendDataToGTM = useGtmEventTracking();

  const validate = (): boolean => {
    const errors: { email?: string; password?: string } = {};
    if (!email || email.length < 4) errors.email = 'Please enter a valid email';
    if (!password || password.length < 4) errors.password = 'Please enter your password';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    setNetworkError(undefined);

    // Clear any existing session before logging in to prevent stale
    // admin/customer state when switching between account types
    localStorage.removeItem('user');
    delete axiosKK.defaults.headers.common['Authorization'];

    axiosKK
      .post<{ user: User; customer: User; jwt: string }>('/login', {
        email,
        password,
      })
      .then((response) => {
        sendDataToGTM({ event: 'account_login' });
        const userResponse = response.data.customer || response.data.user;
        localStorage.setItem(
          'user',
          JSON.stringify({
            ...userResponse,
            token: response.data.jwt,
          }),
        );
        axiosKK.defaults.headers.common['Authorization'] =
          'bearer ' + response.data.jwt;
        postLogin(userResponse);
        setTimeout(() => {
          if (confirmationState && confirmationState.fromConfirmation) {
            history.goBack();
          } else {
            history.push(
              adminCheck(userResponse) ? '/admin/dashboard' : '/storage/boxes',
            );
          }
        }, 100);
      })
      .catch((error) => {
        errorMessage(setNetworkError)(error);
        setIsSubmitting(false);
      });
  };

  return (
    <div className="auth-card">
      <Button
        variant="tertiary"
        size="sm"
        iconOnly
        aria-label="Back"
        className="auth-card__back"
        onClick={() => history.goBack()}
      >
        <ArrowLeftIcon aria-hidden="true" />
      </Button>

      {close && (
        <div className="auth-card__close">
          <Button
            variant="tertiary"
            size="sm"
            iconOnly
            aria-label="Close"
            onClick={close}
          >
            <XMarkIcon aria-hidden="true" />
          </Button>
        </div>
      )}

      <div className="auth-card__header">
        <h3>Welcome Back</h3>
        <p>Log in to manage your storage and orders</p>
      </div>

      {networkError && (
        <div className="auth-card__error">{networkError}</div>
      )}

      <form className="auth-card__form" onSubmit={handleSubmit}>
        <div className="auth-card__field">
          <label htmlFor="login-email">Email</label>
          <input
            id="login-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
          />
          {fieldErrors.email && (
            <span className="field-error">{fieldErrors.email}</span>
          )}
        </div>

        <div className="auth-card__field">
          <label htmlFor="login-password">Password</label>
          <input
            id="login-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password"
          />
          {fieldErrors.password && (
            <span className="field-error">{fieldErrors.password}</span>
          )}
        </div>

        <div className="auth-card__forgot-link">
          <Link to="/forgot-password">Forgot password?</Link>
        </div>

        <SubmitButton disabled={isSubmitting} isSubmitting={isSubmitting}>
          Log In
        </SubmitButton>
      </form>

      {showToggle && (
        <p className="auth-card__toggle">
          Don&apos;t have an account?{' '}
          <button type="button" onClick={() => history.push('/register')}>
            Register
          </button>
        </p>
      )}
    </div>
  );
}
